import React, { useState, useEffect, useRef } from "react";
import { Form, Select, DatePicker, Button } from "antd";
import Chart from "chart.js/auto";
import { useDispatch, useSelector } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { getClusters } from "../features/cluster/clusterSlice";
import { getNewPlacementList } from "../features/placements/placementSlice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PlacementPlan = () => {
  const chartInstanceRef = useRef(null);
  const chartInstance = useRef(null);
  const dispatch = useDispatch();
  const [selectedState, setSelectedState] = useState("");
  const [selectedCluster, setSelectedCluster] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pdfExportRef = useRef(null);

  useEffect(() => {
    dispatch(getStates());
    dispatch(getClusters());
    dispatch(getNewPlacementList());
  }, [dispatch]);

  const statelistState = useSelector(
    (state) => state.statecity.statelist || []
  );
  const clusterlistState = useSelector((state) => state.cluster.cluster || []);
  const placementState = useSelector(
    (state) => state.placementList.placements || []
  );

  // Log placementState to debug
  useEffect(() => {
    console.log("placementState:", placementState);
  }, [placementState]);

  // Process data for chart and table
  const processData = (data, filters = {}) => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [], tableData: [] };
    }

    const { state, cluster, start_date, end_date } = filters;

    const filtered = data.filter((item) => {
      const stateMatch = !state || item._id.state === state;
      const clusterMatch = !cluster || item._id.cluster === cluster;
      const dateMatch =
        !start_date ||
        !end_date ||
        (new Date(item.createdAt) >= new Date(start_date) &&
          new Date(item.createdAt) <= new Date(end_date));
      return stateMatch && clusterMatch && dateMatch;
    });

    const stateWiseData = filtered.reduce((acc, data) => {
      const state = data._id.state || "Unknown";
      acc[state] = acc[state] || { eligible: 0, placed: 0, notPlaced: 0 };
      acc[state].eligible += data.totalEligibleStudents || 0;
      acc[state].placed += data.placedCount || 0;
      acc[state].notPlaced += data.notPlacedCount || 0;
      return acc;
    }, {});

    const labels = Object.keys(stateWiseData);
    const datasets = [
      {
        label: "Eligible Students",
        data: labels.map((state) => stateWiseData[state].eligible),
        backgroundColor: "orange",
      },
      {
        label: "Placed Students",
        data: labels.map((state) => stateWiseData[state].placed),
        backgroundColor: "green",
      },
      {
        label: "Not Placed",
        data: labels.map((state) => stateWiseData[state].notPlaced),
        backgroundColor: "red",
      },
    ];

    const tableData = Object.entries(stateWiseData).map(([state, data]) => ({
      state,
      eligible: data.eligible,
      placed: data.placed,
      notPlaced: data.notPlaced,
    }));

    const result = { labels, datasets, tableData };
    console.log("Processed Data:", result); // Log processed data
    return result;
  };

  // Set initial data
  useEffect(() => {
    if (placementState.length) {
      const { labels, datasets, tableData } = processData(placementState);
      setFilteredData(tableData);
      setChartData({ labels, datasets });
    }
  }, [placementState]);

  const handleSubmit = (values) => {
    const filters = {
      state: values.state,
      cluster: values.cluster,
      start_date: values.start_date
        ? values.start_date.format("YYYY-MM-DD")
        : null,
      end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
    };

    const { labels, datasets, tableData } = processData(
      placementState,
      filters
    );
    setFilteredData(tableData);
    setChartData({ labels, datasets });
  };

  const resetFilters = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedCluster("");
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    const { labels, datasets, tableData } = processData(placementState);
    setFilteredData(tableData);
    setChartData({ labels, datasets });
  };

  // Chart rendering
  useEffect(() => {
    if (chartInstanceRef.current && chartData) {
      const ctx = chartInstanceRef.current.getContext("2d");
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: chartData,
        options: {
          responsive: true,
          plugins: { legend: { position: "top" } },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: "Students" },
            },
            x: { title: { display: true, text: "States" } },
          },
        },
      });
      console.log("Chart Rendered with Data:", chartData); // Log chart data
    }
  }, [chartData]);

  // Pagination logic
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1); // Reset to first page when size changes
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const totalPages = Math.ceil(filteredData.length / pageSize);

  const exportAsPDF = () => {
    const input = pdfExportRef.current;
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("placement_plan_report.pdf");
    });
  };

  const styles = {
    button: {
      padding: "8px 12px",
      color: "#fff",
      border: "none",
      cursor: "pointer",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    tableHeader: {
      backgroundColor: "#e5e7eb",
    },
    tableCell: {
      padding: "0.5rem",
      textAlign: "center",
    },
  };

  return (
    <div
      style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
      ref={pdfExportRef}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4
          style={{
            textAlign: "left",
            fontWeight: "bold",
            fontSize: "large",
            marginBottom: "0.5rem",
          }}
        >
          Placement Planned vs Actual
        </h4>
        <p
          style={{
            color: "#6b7280",
            fontSize: "0.875rem",
            textAlign: "right",
          }}
        >
          Last Updated: 10:30 AM
        </p>
      </div>

      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginTop: "20px", marginLeft: "70px", gap: "10px" }}
      >
        <Form.Item
          name="state"
          rules={[{ required: true, message: "Please select a state!" }]}
        >
          <Select
            placeholder="Select State"
            onChange={(value) => setSelectedState(value)}
            value={selectedState}
            style={{ width: 150 }}
          >
            {statelistState.map((state) => (
              <Select.Option key={state.code} value={state.code}>
                {state.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="cluster">
          <Select
            placeholder="Select Cluster"
            style={{ width: 150 }}
            onChange={(value) => setSelectedCluster(value)}
          >
            {clusterlistState.map((cluster) => (
              <Select.Option key={cluster._id} value={cluster._id}>
                {cluster.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="start_date"
          rules={[{ required: true, message: "Select date!" }]}
        >
          <DatePicker
            placeholder="From"
            style={{ width: 100 }}
            onChange={(value) => {
              setSelectedStartDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>
        <Form.Item
          name="end_date"
          rules={[{ required: true, message: "Select date!" }]}
        >
          <DatePicker
            placeholder="To"
            style={{ width: 100 }}
            onChange={(value) => {
              setSelectedEndDate(value ? value.format("YYYY-MM-DD") : null);
            }}
          />
        </Form.Item>
        <Form.Item>
          <Button
            style={{
              backgroundColor: "#28a745",
              color: "#fff",
              marginTop: 16,
              marginLeft: 330,
            }}
            htmlType="submit"
          >
            Apply Filter
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            style={{
              backgroundColor: "#dc3545",
              color: "#fff",
              marginTop: 16,
              marginLeft: -12,
            }}
            onClick={resetFilters}
          >
            Reset Filter
          </Button>
        </Form.Item>
      </Form>

      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0.5rem",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          padding: "1rem",
          marginTop: "1.5rem",
          height: "auto",
          width: "auto",
        }}
      >
        <canvas ref={chartInstanceRef} height="100%" width="100%"></canvas>
      </div>

      <div
        className="table-container"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0.5rem",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          padding: "1rem",
          marginTop: "1.5rem",
        }}
      >
        <div
          style={{
            marginBottom: "10px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>
            Showing {paginatedData.length} of {filteredData.length} entries
          </div>
          <div>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 100 }}
            >
              <Select.Option value={10}>10</Select.Option>
              <Select.Option value={20}>20</Select.Option>
              <Select.Option value={50}>50</Select.Option>
              <Select.Option value={100}>100</Select.Option>
            </Select>
          </div>
        </div>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableCell}>State</th>
              <th style={styles.tableCell}>No. of Students Eligible</th>
              <th style={styles.tableCell}>No. of Students Placed</th>
              <th style={styles.tableCell}>No. of Students Not Placed</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((data, index) => (
              <tr key={index}>
                <td style={styles.tableCell}>{data.state}</td>
                <td style={styles.tableCell}>{data.eligible}</td>
                <td style={styles.tableCell}>{data.placed}</td>
                <td style={styles.tableCell}>{data.notPlaced}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: "10px", textAlign: "center" }}>
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ marginRight: "10px" }}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ marginLeft: "10px" }}
          >
            Next
          </Button>
        </div>
      </div>

      <div style={{ marginTop: "20px", display: "flex", gap: "15px" }}>
        <button
          style={{
            ...styles.button,
            backgroundColor: "#3B82F6",
            borderRadius: "5px",
          }}
          onClick={exportAsPDF}
        >
          Export as PDF
        </button>
        <button
          style={{
            ...styles.button,
            backgroundColor: "#3B82F6",
            borderRadius: "5px",
          }}
        >
          Export as CSV
        </button>
      </div>
    </div>
  );
};

export default PlacementPlan;
